import React, { useEffect, useRef, useState } from "react";
import { Button, Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import "../ReportDetail.scss";

/* ---------- Types ---------- */
export type ReportHeaderData = {
    id: string;
    title: string;
    categoryKey: string;
    categoryTitle: string;
    isNew: boolean;
};

type ReportsDetailHeaderProps = {
    data: ReportHeaderData;
    onSave?: () => void;
    onSaveAs?: () => void;
    onCancel?: () => void;
    onExport?: () => void;
    exportDisabled?: boolean;
    showExport?: boolean;
    showSaveActions?: boolean;
    showCancel?: boolean;
    saveLoading?: boolean;
};

const ReportsDetailHeader: React.FC<ReportsDetailHeaderProps> = ({
    data,
    onSave,
    onSaveAs,
    onCancel,
    onExport,
    exportDisabled = true,
    showExport = true,
    showSaveActions = true,
    showCancel = true,
    saveLoading = false,
}) => {
    const [saveMenuOpen, setSaveMenuOpen] = useState(false);
    const saveMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!saveMenuOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                saveMenuRef.current &&
                !saveMenuRef.current.contains(event.target as Node)
            ) {
                setSaveMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [saveMenuOpen]);

    return (
        <header className="rd-header">
            <div className="rd-header__main">
                <h1 className="rd-header__title">
                    {data.title || "Untitled"}
                </h1>

                <div className="rd-header__actions">
                    {showExport && (
                        <Button
                            variant="secondary"
                            color="primary"
                            label={
                                <span className="btn-with-icon">
                                    Export Report
                                    <Iconography icon="upload" size={IconographySizes.Small} />
                                </span>
                            }
                            style={exportDisabled ? { pointerEvents: "none", opacity: 0.5 } : undefined}
                            onClick={onExport}
                        />
                    )}

                    {showCancel && (
                        <Button
                            variant="secondary"
                            color="primary"
                            label="Cancel"
                            onClick={onCancel}
                        />
                    )}

                    {showSaveActions && (
                        <div className="rd-save-split" ref={saveMenuRef}>
                            <Button
                                appearance="filled"
                                color="primary"
                                label="Save"
                                disabled={saveLoading}
                                onClick={onSave}
                            />
                            <button
                                type="button"
                                className="rd-save-split__toggle"
                                aria-label="Save options"
                                aria-expanded={saveMenuOpen}
                                disabled={saveLoading}
                                onClick={() => setSaveMenuOpen((open) => !open)}
                            >
                                <Iconography icon="angleDown" size={IconographySizes.Small} />
                            </button>
                            {saveMenuOpen && (
                                <ul className="rd-save-split__menu" role="menu">
                                    <li role="none">
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                setSaveMenuOpen(false);
                                                onSaveAs?.();
                                            }}
                                        >
                                            Save As
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default ReportsDetailHeader;
